import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import { click1, click2 } from './click_audio_base64';
import fixIOSAudioApiPlayback from '../../../utils/fixIOSAudioApiPlayback';

const useStyles = makeStyles()((theme) => ({
  metronome: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  beatIndicator: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  beat: {
    width: '1rem',
    height: '1rem',
    borderRadius: '50%',
    backgroundColor: theme.palette.grey[500],
  },
  activeBeat: {
    backgroundColor: theme.palette.grey[700],
  },
  firstActiveBeat: {
    backgroundColor: theme.palette.primary.main,
  },
}));

export interface MetronomeProps {
  bpm: number;
  timeSig: number;
  isOn: boolean;
  onTick?: (currentTick: number) => void;
}

const Metronome: React.FC<MetronomeProps> = ({ bpm, timeSig, isOn, onTick }) => {
  const { classes } = useStyles();

  const [currentBeat, setCurrentBeat] = React.useState(0);

  const beatInterval = 60000 / bpm;

  const audioContext = React.useRef<AudioContext | null>(null);
  const click1Buffer = React.useRef<AudioBuffer | null>(null);
  const click2Buffer = React.useRef<AudioBuffer | null>(null);
  const animationFrameId = React.useRef<number>();
  const nextTickTime = React.useRef<number>(0);

  React.useEffect(() => {
    fixIOSAudioApiPlayback();

    audioContext.current = new AudioContext();

    const loadAudioBuffer = async (base64: string) => {
      const response = await fetch(base64);
      const arrayBuffer = await response.arrayBuffer();
      return audioContext.current?.decodeAudioData(arrayBuffer);
    };

    loadAudioBuffer(click1).then((buffer) => {
      click1Buffer.current = buffer;
    });
    loadAudioBuffer(click2).then((buffer) => {
      click2Buffer.current = buffer;
    });

    return () => {
      audioContext.current?.close();
    };
  }, [isOn]);

  const playTick = (buffer: AudioBuffer | null) => {
    if (audioContext.current && buffer) {
      const source = audioContext.current.createBufferSource();
      source.buffer = buffer;
      source.connect(audioContext.current.destination);
      source.start(0);
    }
  };

  React.useEffect(() => {
    let startTime = 0;

    const tick = (timestamp: number) => {
      if (!startTime) {
        startTime = timestamp;
        nextTickTime.current = timestamp + beatInterval;
      }

      if (timestamp >= nextTickTime.current) {
        const nextBeat = (currentBeat + 1) % timeSig;
        playTick(nextBeat === 0 ? click1Buffer.current : click2Buffer.current);
        if (onTick) {
          onTick(nextBeat);
        }
        setCurrentBeat(nextBeat);
        nextTickTime.current += beatInterval;
      }

      if (isOn) {
        animationFrameId.current = requestAnimationFrame(tick);
      }
    };

    if (isOn) {
      nextTickTime.current = 0;

      animationFrameId.current = requestAnimationFrame(tick);
    } else {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
      setCurrentBeat(0);
    }

    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [isOn, timeSig, beatInterval, currentBeat, onTick]);

  return (
    <div className={classes.metronome}>
      <div className={classes.beatIndicator}>
        {Array.from({ length: timeSig }).map((_, i) => {
          let activeClass = '';
          if (currentBeat === i) {
            if (i === 0) {
              activeClass = classes.firstActiveBeat;
            } else {
              activeClass = classes.activeBeat;
            }
          }

          // eslint-disable-next-line react/no-array-index-key
          return <div key={`beat-${i}`} className={`${classes.beat} ${activeClass}`} />;
        })}
      </div>
    </div>
  );
};

export default Metronome;
